export const TenantPrivacyBenefits = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 text-gray-900">
            Protect Your Tenants' Privacy with Direct Sales
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Selling to a direct buyer eliminates the need for countless showings, protecting your tenants from disruption and maintaining their quality of life
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/3df901b30d8fac270858b54cfd90177a.jpg"
              alt="Tenant privacy protected during apartment building sale"
              className="w-full h-full rounded-lg shadow-xl object-cover"
            />
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-6 text-gray-900">
              Why Tenant Privacy Matters in Apartment Building Sales
            </h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              When you list your apartment building with a traditional agent, you're committing to multiple showings with different potential buyers, inspectors, appraisers, and agents. Each showing disrupts your tenants' lives and can create anxiety about their housing situation.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              With a direct sale to an experienced buyer, you typically need only one or two property walkthroughs. Professional investors understand the property type and can make decisions quickly without repeatedly disturbing your tenants.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-user-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Minimal Disruption</h4>
                  <p className="text-gray-700">One or two showings instead of dozens over several months</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-check-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Scheduled Convenience</h4>
                  <p className="text-gray-700">Work with tenants to find mutually convenient times for the single walkthrough</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Privacy Protection</h4>
                  <p className="text-gray-700">No strangers constantly walking through their homes and personal spaces</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
          <h3 className="text-2xl font-bold mb-8 text-gray-900 text-center">
            Traditional Listing vs. Direct Sale: Tenant Impact Comparison
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border-2 border-red-200 rounded-lg p-6 bg-red-50">
              <h4 className="text-xl font-bold mb-4 text-red-900 flex items-center gap-2">
                <i className="ri-close-circle-line text-2xl"></i>
                Traditional Agent Listing
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">15-30+ showings over 3-6 months</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Multiple open houses with strangers touring units</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Constant notice requirements and scheduling conflicts</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Tenants feel anxious about their housing future</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Risk of tenants moving out during sale process</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Property listed publicly on MLS and websites</span>
                </li>
              </ul>
            </div>
            <div className="border-2 border-teal-200 rounded-lg p-6 bg-teal-50">
              <h4 className="text-xl font-bold mb-4 text-teal-900 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-2xl"></i>
                Direct Buyer Sale (FSBO)
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">1-2 showings total with serious buyer</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">No open houses or unqualified lookers</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Minimal notice required, scheduled at tenant convenience</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Tenants remain calm with minimal disruption</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Tenant retention remains high throughout process</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Private sale keeps tenants unaware if desired</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};